import { BusinessKeywordsMapping } from 'api/client/mapping/business-keywords-mapping';
import { IQuestionCreateDTO, IQuestionDetailDTO, IQuestionUpdateBaseDTO, IQuestionUpdateDTO, QuestionCountryModel, QuestionCreateDTO, QuestionsClient, QuestionUpdateDTO } from 'api/swagger/ApiClient';
import { autoinject, BindingEngine, bindingMode, customElement, newInstance } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ValidationController, ValidationRules } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { WidgetButton } from 'controls/as-widget-stack';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { QuestionDetailModel } from 'model/forms/question-detail-data';
import { CountryWrapper } from 'model/support-types';
import { ModelBase } from 'support/base_classes/model-base';
import { HOME_ROUTE } from 'support/settings';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { EventHelper } from 'framework/aurelia_tools/event-helper';

@autoinject
@customElement('question-panel')
export class QuestionPanelCustomElement extends ModelBase<IQuestionDetailDTO> {
  @bindable questionDetailModel: QuestionDetailModel;
  backup: IQuestionDetailDTO = null;
  @bindable filterText: string = null;
  newEmptyCountryWrapper: CountryWrapper = { country: null };
  @bindable({ defaultBindingMode: bindingMode.fromView }) isCountryEditing = false;
  @bindable cancelNew: () => void = null;
  countryButtons: WidgetButton[] = [{
    icon: "fa-globe-africa",
    title: "Manage country",
    onClick: (item: CountryWrapper) => {
      this.router.navigate(`#/${HOME_ROUTE}/admin/countries/${item.country.id}`);
    },
  }];
  archiveButtonVisible = false;
  restoreButtonVisible = false;
  submitButtonVisible = false;
  cancelButtonVisible = false;

  constructor(private router: Router, private api: QuestionsClient, bindingEngine: BindingEngine, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private businessKeywordsMapping: BusinessKeywordsMapping, private validationHelper: ValidationHelper, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer, private eventHelper: EventHelper) {
    super(bindingEngine);
    validationRenderer.allowedParents = 'bs-form-input, bs-form-lookup, bs-form-select, bs-form-textarea';
    this.validationController.addRenderer(validationRenderer);
  }

  bind() {
    this.model = this.questionDetailModel.data;
    super.bind();
    this.backup = cloneDeep(this.model);
  }

  modelChanged(newValue: IQuestionDetailDTO) {
    super.modelChanged(newValue);
    this.newEmptyCountryWrapper = { country: null } as CountryWrapper;
    ValidationRules
      .ensureObject<IQuestionDetailDTO>().required()
      .ensure(model => model.name).displayName('Question').required()
      .on(this.model);
    this.validationController.reset();
    this.updateButons();
  }

  async submit() {
    await this.validationHelper.validate(this.validationController) && await this.save();
  }

  async cancel() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      if (this.model.id) {
        this.changed && this.rebindData(this.backup);
      }
      else {
        this.cancelNew();
      }
    }
  }

  private rebindData(dto: IQuestionDetailDTO) {
    this.questionDetailModel.data = dto;
    this.model = dto;
    this.backup = cloneDeep(this.model);
    this.questionDetailModel.questionDataChanged(this.questionDetailModel);
  }

  async archive() {
    this.rebindData(await this.api.archiveQuestion(this.model.id));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Question has been successfully <b>archived</b>.', 'Archived', ToastType.Success, ToastFormat.Html);
  }

  async restore() {
    this.rebindData(await this.api.unarchiveQuestion(this.model.id));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Question has been successfully <b>restored</b>.', 'Restored', ToastType.Success, ToastFormat.Html);
  }

  private async save() {
    const data = this.getSaveData(this.model);
    let dto = null as IQuestionDetailDTO;
    if (this.model.id)
      dto = await this.api.updateQuestion(this.model.id, new QuestionUpdateDTO(data as IQuestionUpdateDTO));
    else
      dto = await this.api.createQuestion(new QuestionCreateDTO(data as IQuestionCreateDTO))
    this.rebindData(dto);
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Question has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
  }

  getSaveData(model: IQuestionDetailDTO): IQuestionUpdateBaseDTO {
    return {
      name: model.name,
      isDefaultChecked: model.isDefaultChecked,
      isMandatory: model.isMandatory
    }
  }

  addNewCountry(item: CountryWrapper): Promise<void> {
    return this.api.addQuestionCountry(this.model.id, new QuestionCountryModel({ countryId: item.country.id }))
      .then((dto) => {
        this.rebindQuestionDetailModel(dto);
        this.eventHelper.publish('settings-changed');
        this.toastHelper.toast('Country has been <b>added</b>.', 'Country added', ToastType.Success, ToastFormat.Html);
      });
  }

  removeCountry(item: CountryWrapper): Promise<void> {
    return this.api.removeQuestionCountry(this.model.id, new QuestionCountryModel({ countryId: item.country.id }))
      .then((dto) => {
        this.rebindQuestionDetailModel(dto);
        this.eventHelper.publish('settings-changed');
        this.toastHelper.toast('Country has been <b>removed</b>.', 'Country removed', ToastType.Success, ToastFormat.Html);
      });
  }

  private rebindQuestionDetailModel(dto: IQuestionDetailDTO) {
    this.questionDetailModel.countriesWrapper = dto.countries.map(x => ({ country: this.businessKeywordsMapping.mapToCountry(x) } as CountryWrapper));
    this.questionDetailModel.isGlobal = dto.isGlobal;
    this.questionDetailModel.questionDataChanged(this.questionDetailModel);
  }

  @watch((x: QuestionPanelCustomElement) => x.changed)
  @watch((x: QuestionPanelCustomElement) => x.isCountryEditing)
  private updateModel() {
    this.updateButons();
    this.questionDetailModel.isQuestionEditing = this.changed || this.isCountryEditing;
  }

  private updateButons() {
    this.submitButtonVisible = this.changed || !this.model.id;
    this.cancelButtonVisible = this.changed || !this.model.id;
    this.archiveButtonVisible = this.model.id && this.model.isActive;
    this.restoreButtonVisible = this.model.id && !this.model.isActive;
  }
}
